import React from 'react';
import { Box } from '@mui/material';

import { InformationBlock, Page } from 'components';
import { capitalizeFirstLetter } from 'core/shared';
import { useToast } from 'core/toast/hooks';
import { useNavigate } from 'hooks/useNavigate';
import { useParams } from 'hooks/useParams';
import { useTranslations } from 'hooks/useTranslations';

import { CustomString } from 'config/translations';
import { UseMutation, UseQuery } from 'useQuery';
import { type TransactionRuleType } from 'useQuery/types';

function TransactionRuleGroupPage() {
  const { navigate } = useNavigate();
  const { addToast } = useToast();
  const translations = useTranslations('transactionRuleGroupPage');
  const params = useParams('transactionRuleGroup');
  const id = params.id;

  const {
    data: transactionRuleGroup,
    refetch,
    isLoading: loading,
  } = UseQuery.transactionRuleGroup(id);
  const { data: merchantCategoryCodesData } = UseQuery.merchantCategoryCodes();
  const { mutateAsync: mutateAsyncAddTransactionRuleGroup, isLoading: loadingAdd } =
    UseMutation.addTransactionRuleGroup();
  const { mutateAsync: mutateAsyncUpdateTransactionRuleGroup, isLoading: loadingUpdate } =
    UseMutation.updateTransactionRuleGroup();
  const { mutateAsync: mutateAsyncDeleteTransactionRuleGroup, isLoading: loadingDelete } =
    UseMutation.deleteTransactionRuleGroup();

  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [type, setType] = React.useState<TransactionRuleType>('MERCHANT_CATEGORY_CODES');
  const [codes, setCodes] = React.useState<string[]>([]);

  const hasNewData =
    (id === 'new' && name.trim() !== '' && codes.length > 0) ||
    (id !== 'new' &&
      (name !== transactionRuleGroup?.name ||
        description !== transactionRuleGroup?.description ||
        codes !== transactionRuleGroup?.rule?.codes));

  function restoreData() {
    if (transactionRuleGroup) {
      setName(transactionRuleGroup.name);
      setDescription(transactionRuleGroup.description);
      setType(transactionRuleGroup.type);
      setCodes(transactionRuleGroup.rule?.codes ?? []);
    }
  }

  React.useEffect(() => {
    restoreData();
  }, [transactionRuleGroup]);

  return (
    <Page
      name="transactionRuleGroup"
      actions={[
        {
          title: translations.delete,
          mode: 'closeAfterLoading',
          svgName: 'delete',
          onClick: async () => {
            try {
              await mutateAsyncDeleteTransactionRuleGroup(id);

              addToast({ title: translations.successfullyDeleted, severity: 'success' });

              navigate({
                name: 'transactionRuleGroups',
                id: undefined,
                searchParams: { page: 1 },
                metaKey: false,
              });
            } catch {
              // nothing
            }
          },
          loading: loadingDelete,
          disabled: id === 'new' || loadingDelete,
        },
      ]}
      footer={[
        {
          label: translations.restore,
          variant: 'outlined',
          onClick: () => restoreData(),
          disabled: id === 'new' || !hasNewData,
        },
        {
          label: id === 'new' ? translations.save : translations.update,
          onClick: async () => {
            try {
              if (id === 'new') {
                const { result } = await mutateAsyncAddTransactionRuleGroup({
                  name,
                  description,
                  type: 'MERCHANT_CATEGORY_CODES',
                  rule: { codes },
                });

                addToast({ title: translations.successfullyAdded, severity: 'success' });

                if (result.id) {
                  navigate({
                    name: 'transactionRuleGroup',
                    id: result.id,
                    searchParams: undefined,
                    metaKey: false,
                  });
                } else {
                  navigate({
                    name: 'transactionRuleGroups',
                    id: undefined,
                    searchParams: { page: 1 },
                    metaKey: false,
                  });
                }
              } else {
                await mutateAsyncUpdateTransactionRuleGroup({
                  id,
                  name,
                  description,
                  rule: { codes },
                });

                await refetch();

                addToast({ title: translations.successfullyUpdated, severity: 'success' });
              }
            } catch (error) {
              // Do nothing
            }
          },
          disabled: !hasNewData,
          loading: loadingAdd || loadingUpdate,
        },
      ]}
      loading={loading}
    >
      <Box>
        <InformationBlock
          items={[
            {
              label: translations.name,
              data: { value: name, onChange: setName, onEnter: () => {}, required: true },
              type: 'editName',
              fullWidth: true,
            },
            {
              label: translations.description,
              data: {
                value: description,
                onChange: setDescription,
                onEnter: () => {},
                required: false,
              },
              type: 'editName',
              fullWidth: true,
            },
            {
              label: translations.type,
              data: {
                value: capitalizeFirstLetter(
                  ((type ?? 'MERCHANT_CATEGORY_CODES') as TransactionRuleType)
                    .toLowerCase()
                    .replaceAll('_', ' '),
                ),
              },
              type: 'text',
              fullWidth: true,
            },
            {
              label: translations.codes,
              fullWidth: true,
              data: {
                label: translations.codes,
                values: codes as string[],
                options: Object.keys(merchantCategoryCodesData?.result ?? {}).map((id) => ({
                  id,
                  label: `${id}: ${
                    merchantCategoryCodesData?.result[id] as unknown as string
                  }` as CustomString,
                })),
                onChange: setCodes,
                mode: 'multiple',
              },
              type: 'select_1',
            },
          ]}
        />
      </Box>
    </Page>
  );
}

export { TransactionRuleGroupPage };
